<script setup>
import { ref, computed, defineProps, defineEmits, watch } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { useSalidaStore } from "@/store/useSalidaStore";
import { useCatalogos } from "@/composables/useCatalogos";
import { useAppToast } from "@/composables/useAppToast";

const props = defineProps({
  modelValue: [String, Number],
  placeholder: {
    type: String,
    default: "Seleccione conductor",
  },
});
const emit = defineEmits(["update:modelValue"]);

const salida = useSalidaStore();
const cat = useCatalogos();
const toast = useAppToast();

const visible = ref(false);
const filtro = ref("");
const nuevoNombre = ref("");
const saving = ref(false);

const conductorSeleccionado = computed(() =>
  salida.conductores.find((c) => c.id === props.modelValue)
);

const conductoresOrdenados = computed(() =>
  salida.conductores.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const conductoresFiltrados = computed(() => {
  const q = filtro.value.trim().toLowerCase();
  if (!q) return conductoresOrdenados.value;
  return conductoresOrdenados.value.filter((c) =>
    (c.name || "").toLowerCase().includes(q)
  );
});

const open = () => {
  filtro.value = "";
  nuevoNombre.value = "";
  visible.value = true;
};

const seleccionar = (id) => {
  emit("update:modelValue", id);
  visible.value = false;
};

// Precarga el nombre a crear con lo que la persona ya escribió en el buscador,
// para no tener que retipearlo si el conductor no aparece en el listado.
watch(filtro, (q) => {
  nuevoNombre.value = q;
});

const agregarConductor = async () => {
  const name = nuevoNombre.value.trim();
  if (!name) {
    toast.warn("Falta el nombre", "Ingrese el nombre del conductor.");
    return;
  }

  saving.value = true;
  try {
    const created = await cat.createConductor({ name });
    if (created?.error) {
      toast.error("Ocurrió un error", created.error);
      return;
    }
    const conductor = { id: created.id, name: created.name ?? name };
    salida.conductores.push(conductor);
    seleccionar(conductor.id);
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="w-full">
    <Button
      type="button"
      :label="conductorSeleccionado?.name || placeholder"
      icon="pi pi-user"
      outlined
      class="w-full justify-content-start"
      @click="open"
    />

    <Dialog
      v-model:visible="visible"
      modal
      header="Seleccionar conductor"
      :style="{ width: '420px' }"
    >
      <div class="flex flex-column gap-3">
        <InputText
          v-model="filtro"
          placeholder="Buscar conductor..."
          autofocus
          class="w-full"
        />

        <div class="conductor-list">
          <div
            v-for="c in conductoresFiltrados"
            :key="c.id"
            class="conductor-row"
            :class="{ selected: c.id === modelValue }"
            @click="seleccionar(c.id)"
          >
            <span>{{ c.name }}</span>
            <i v-if="c.id === modelValue" class="pi pi-check" />
          </div>
          <div v-if="!conductoresFiltrados.length" class="empty-state">
            Ningún conductor coincide con la búsqueda.
          </div>
        </div>

        <div class="add-new">
          <label class="pb-1 text-sm">¿No aparece? Agregar conductor nuevo</label>
          <div class="flex gap-2">
            <InputText
              v-model="nuevoNombre"
              placeholder="Nombre del conductor"
              class="flex-1"
              @keyup.enter="agregarConductor"
            />
            <Button
              icon="pi pi-plus"
              label="Agregar"
              :loading="saving"
              @click="agregarConductor"
            />
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Cerrar" text @click="visible = false" />
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.conductor-list {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  max-height: 260px;
  overflow-y: auto;
}

.conductor-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
}

.conductor-row:hover {
  background: var(--surface-100);
}

.conductor-row.selected {
  background: var(--surface-200);
  font-weight: bold;
}

.empty-state {
  font-size: var(--text-callout);
  color: var(--label-tertiary);
  padding: 0.5rem 0;
  font-style: italic;
}

.add-new {
  border-top: 1px solid var(--surface-border);
  padding-top: 0.75rem;
}
</style>
